import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

const isResultOk = (res: any): boolean =>
  res?.result_code === '200' || res?.resultCode === 200;

interface FavoritesState {
  // 약국이 즐겨찾기로 지정한 환자 user_id 목록
  // (Set은 zustand persist에서 직렬화 안 되므로 number[]로 보관)
  userIds: number[];
  isLoading: boolean;

  // 헬퍼
  has: (userId: number | null | undefined) => boolean;

  // 액션
  fetchAll: (brn: string) => Promise<void>;
  toggle: (brn: string, userId: number) => Promise<void>;
  clear: () => void;
}

/**
 * 약국별 즐겨찾는 환자 store
 *
 * 사용 흐름:
 * 1. 로그인 성공 시 authStore.login → fetchAll(brn) 호출 (한 번만)
 * 2. 컴포넌트는 has(user_id)로 즐겨찾기 여부만 확인
 * 3. 별 토글 시 toggle(brn, userId) → store 즉시 update + API 호출 (단방향)
 * 4. 실패 시 store 자동 rollback
 */
const useFavoritesStore = create<FavoritesState>()(
  devtools(
    persist(
      (set, get) => ({
        userIds: [],
        isLoading: false,

        has: (userId) => {
          if (userId == null) return false;
          return get().userIds.includes(userId);
        },

        fetchAll: async (brn: string) => {
          if (!brn) return;
          set({ isLoading: true });
          try {
            const { getFavoritesService } = await import(
              '../../domains/favorites'
            );
            const res = await getFavoritesService().getFavorites(brn);
            if (isResultOk(res)) {
              const list: number[] = res?.data?.user_ids ?? [];
              set({ userIds: list, isLoading: false });
            } else {
              set({ isLoading: false });
            }
          } catch (err) {
            console.error('[favoritesStore] fetchAll 실패:', err);
            set({ isLoading: false });
          }
        },

        toggle: async (brn: string, userId: number) => {
          if (!brn || !userId) return;
          const wasFavorite = get().userIds.includes(userId);

          // 1. Optimistic UI: store 즉시 업데이트
          if (wasFavorite) {
            set({ userIds: get().userIds.filter((id) => id !== userId) });
          } else {
            set({ userIds: [...get().userIds, userId] });
          }

          // 2. 단방향 API 호출 (재조회 없음)
          try {
            const { getFavoritesService } = await import(
              '../../domains/favorites'
            );
            const svc = getFavoritesService();
            if (wasFavorite) {
              await svc.removeFavorite(brn, userId);
            } else {
              await svc.addFavorite(brn, userId);
            }
          } catch (err) {
            // 3. 실패 시 rollback
            console.error('[favoritesStore] toggle 실패 — rollback:', err);
            if (wasFavorite) {
              set({ userIds: [...get().userIds, userId] });
            } else {
              set({ userIds: get().userIds.filter((id) => id !== userId) });
            }
          }
        },

        clear: () => {
          set({ userIds: [], isLoading: false });
        },
      }),
      {
        name: 'pharmacy-favorites-storage',
        partialize: (state) => ({ userIds: state.userIds }),
      },
    ),
    {
      name: 'pharmacy-favorites-store',
      enabled: import.meta.env.DEV,
    },
  ),
);

export default useFavoritesStore;
